import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Receipt, Plus, DollarSign } from "lucide-react";

const REASON_LABELS = {
  fuel: "Fuel",
  supplies: "Supplies (SH / chemicals)",
  tools: "Tools",
  other: "Other",
};

const STATUS_TABS = [
  { key: "pending",  label: "Pending review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_STYLES = {
  pending:  "bg-amber-50 text-amber-700 border border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border border-rose-200",
};

export default function AdminReimbursements() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // { row, mode: "approve"|"reject" }
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Admin-authored request (on-behalf-of a lead tech) state
  const [leads, setLeads] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    user_id: "",
    store_name: "",
    amount: "",
    reason: "fuel",
    description: "",
    admin_notes: "",
  });
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reimbursements?status=${status}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/employees?active=true");
      setLeads((data || []).filter((e) => e.is_lead_technician));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  const openAdd = () => {
    setAddForm({ user_id: "", store_name: "", amount: "", reason: "fuel", description: "", admin_notes: "" });
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addForm.user_id) return toast.error("Pick a lead technician.");
    if (!addForm.store_name.trim()) return toast.error("Store name is required.");
    const amt = parseFloat(addForm.amount);
    if (!(amt > 0)) return toast.error("Enter a valid amount over $0.");
    setAddSaving(true);
    try {
      await api.post("/admin/reimbursements", {
        user_id: addForm.user_id,
        store_name: addForm.store_name.trim(),
        amount: amt,
        reason: addForm.reason,
        description: addForm.description.trim(),
        admin_notes: addForm.admin_notes.trim(),
      });
      toast.success("Reimbursement added and approved — lands on their next paystub.");
      setAddOpen(false);
      setTab("approved");
      load("approved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAddSaving(false);
    }
  };

  const openAction = (row, mode) => {
    setAction({ row, mode });
    setNotes("");
  };

  const closeAction = () => { setAction(null); setNotes(""); };

  const submitAction = async () => {
    if (!action) return;
    if (action.mode === "reject" && !notes.trim()) {
      return toast.error("A reason is required when rejecting.");
    }
    setSaving(true);
    try {
      await api.post(
        `/admin/reimbursements/${action.row.id}/${action.mode}`,
        { admin_notes: notes.trim() }
      );
      toast.success(action.mode === "approve"
        ? "Approved — will land on the next paystub as after-tax pay."
        : "Rejected — the lead will see your reason.");
      closeAction();
      load(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const total = useMemo(
    () => items.reduce((sum, r) => sum + (r.amount || 0), 0),
    [items]
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="admin-reimbursements-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <Receipt className="h-7 w-7 text-sky-600" />
            <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900">Reimbursements</h1>
          </div>
          <Button
            onClick={openAdd}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="admin-add-reimbursement-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add reimbursement
          </Button>
        </div>
        <p className="text-slate-600 mb-6">
          Review and approve out-of-pocket purchase reimbursements from lead techs. Approved amounts
          land on the tech's next paystub as after-tax pay — they get the exact dollar amount back.
          Need to log one yourself? Tap <b>Add reimbursement</b> above.
        </p>

        <div className="flex gap-2 mb-4 border-b border-slate-200 pb-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`reimb-tab-${t.key}`}
              className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition ${
                tab === t.key
                  ? "bg-sky-600 text-white"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto text-sm text-slate-500 self-center">
            {items.length} request{items.length !== 1 ? "s" : ""} · ${total.toFixed(2)} total
          </div>
        </div>

        {loading && <p className="text-slate-500 text-sm">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl bg-white border border-dashed border-slate-300 p-10 text-center text-slate-500">
            Nothing here — {tab === "pending" ? "no requests awaiting review right now." :
              tab === "approved" ? "no approved requests yet." : "no rejected requests."}
          </div>
        )}

        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.id} data-testid={`admin-reimb-row-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </span>
                      <span className="text-sm font-semibold text-slate-700">{r.employee_name}</span>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-500">{new Date(r.submitted_at).toLocaleString()}</span>
                    </div>
                    <div className="text-lg font-bold text-slate-900">
                      {r.store_name} — <span className="text-emerald-700">${r.amount.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      <b>{REASON_LABELS[r.reason] || r.reason}</b>
                      {r.description && <> · {r.description}</>}
                    </div>
                    {r.admin_notes && (
                      <div className="text-xs mt-2 rounded bg-slate-50 border border-slate-200 p-2 text-slate-700">
                        <b>Admin note:</b> {r.admin_notes}
                      </div>
                    )}
                    {r.paid_on_paystub_id && (
                      <div className="text-xs mt-2 text-emerald-700 font-semibold">
                        Paid on paystub · {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ""}
                      </div>
                    )}
                  </div>
                  {r.status === "pending" && (
                    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                      <Button
                        onClick={() => openAction(r, "approve")}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`reimb-approve-btn-${r.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openAction(r, "reject")}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`reimb-reject-btn-${r.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="admin-add-reimb-dialog">
          <DialogHeader>
            <DialogTitle>Add reimbursement</DialogTitle>
            <DialogDescription>
              File a reimbursement on behalf of a lead technician. This is auto-approved and lands on
              their next paystub as after-tax pay — the exact dollar amount, no withholding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="add-reimb-employee">Lead technician</Label>
              <Select
                value={addForm.user_id}
                onValueChange={(v) => setAddForm({ ...addForm, user_id: v })}
              >
                <SelectTrigger id="add-reimb-employee" data-testid="admin-add-reimb-employee">
                  <SelectValue placeholder={leads.length ? "Pick a lead tech…" : "No lead techs — mark someone as a lead first"} />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.first_name} {e.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="add-reimb-store">Store name</Label>
              <Input
                id="add-reimb-store"
                value={addForm.store_name}
                onChange={(e) => setAddForm({ ...addForm, store_name: e.target.value })}
                placeholder="e.g. Home Depot, Shell"
                data-testid="admin-add-reimb-store"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="add-reimb-amount">Amount</Label>
                <div className="relative">
                  <DollarSign className="h-4 w-4 absolute left-2 top-3 text-slate-400" />
                  <Input
                    id="add-reimb-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={addForm.amount}
                    onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="admin-add-reimb-amount"
                  />
                </div>
              </div>
              <div>
                <Label>Reason</Label>
                <Select
                  value={addForm.reason}
                  onValueChange={(v) => setAddForm({ ...addForm, reason: v })}
                >
                  <SelectTrigger data-testid="admin-add-reimb-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASON_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="add-reimb-desc">Notes (optional)</Label>
              <Textarea
                id="add-reimb-desc"
                rows={2}
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                placeholder="What was it for? Which job?"
                data-testid="admin-add-reimb-desc"
              />
            </div>
            <div>
              <Label htmlFor="add-reimb-admin-notes">Admin note (optional)</Label>
              <Textarea
                id="add-reimb-admin-notes"
                rows={2}
                value={addForm.admin_notes}
                onChange={(e) => setAddForm({ ...addForm, admin_notes: e.target.value })}
                placeholder="e.g. Filed for tech — phone died on the job."
                data-testid="admin-add-reimb-admin-notes"
              />
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
              <b>This is auto-approved.</b> The amount will be added to this tech's next paystub as
              after-tax pay. Only lead technicians are eligible.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
            <Button
              onClick={submitAdd}
              disabled={addSaving || !leads.length}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="admin-add-reimb-submit"
            >
              {addSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add & approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!action} onOpenChange={(open) => !open && closeAction()}>
        <DialogContent data-testid="reimb-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action?.mode === "approve" ? "Approve reimbursement" : "Reject reimbursement"}
            </DialogTitle>
          </DialogHeader>
          {action && (
            <div className="space-y-3">
              <div className="rounded bg-slate-50 border border-slate-200 p-3 text-sm">
                <div><b>{action.row.employee_name}</b> — ${action.row.amount.toFixed(2)}</div>
                <div className="text-slate-600 mt-1">
                  {action.row.store_name} · {REASON_LABELS[action.row.reason] || action.row.reason}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">
                  {action.mode === "approve" ? "Note (optional)" : "Reason for rejection (required)"}
                </label>
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={action.mode === "approve"
                    ? "e.g. Verified receipt in office chat."
                    : "e.g. Receipt not attached. Please resubmit with photo."}
                  data-testid="reimb-action-notes"
                />
              </div>
              {action.mode === "approve" && (
                <div className="rounded bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
                  <b>Verify first:</b> confirm you've seen the receipt photo in the office chat and the
                  purchase was legitimate before approving. Once approved, this ${action.row.amount.toFixed(2)}
                  {" "}goes onto the tech's next paystub as after-tax pay.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeAction} disabled={saving}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={saving}
              className={action?.mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
              data-testid="reimb-action-confirm-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {action?.mode === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
